<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="items.items"
      item-key="id"
      class="elevation-0 background-transparent"
      :items-per-page="25"
      :loading="loading"
      :options.sync="options"
      :server-items-length="items.meta.totalItems"
      :footer-props="{
        'items-per-page-options': rowsPerPageItemsGlobal
      }"
    >
      <template v-slot:top>
        <div class="d-inline-flex justify-space-between align-center pr-2 mb-5" style="height: 62px; width: 100%">

          <!-- Left-aligned content -->
          <div class="d-inline-flex align-center pl-2">
            <v-text-field
              v-model="search"
              class="ml-2 custom-search-perm"
              outlined
              hide-details
              clearable
              dense
              :label="$lang.labels.searchByName"
              data-cy="search-by-name"
            ></v-text-field>
          </div>

          <!-- Right-aligned buttons -->
          <div class="d-inline-flex align-center ml-auto">
            <v-btn
              text
              light
              color="primary"
              class="ml-2"
              @click="$emit('fetchModules', { options: options, search: search && search.length > 1 ? search : '', resetPage: true })"
            >
              <v-icon dense small>
                mdi-refresh
              </v-icon>
            </v-btn>
            <div class="d-inline-flex align-center">
              <v-btn
                v-if="roles.includes('MODULE_CREATOR')"
                outlined
                data-cy="module-import"
                color="primary"
                class="mr-2"
                :to="{ name: 'moduleImport', params: { lang: $lang.current_lang } }"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-upload
                </v-icon>
                {{ $lang.actions.import }}</v-btn>
              <v-btn
                v-if="roles.includes('MODULE_CREATOR')"
                outlined
                data-cy="module-create"
                color="accent"
                class="color-accent-text mr-1"
                :to="{ name: 'moduleCreate', params: { lang: $lang.current_lang } }"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-file-plus
                </v-icon>
                {{ $lang.actions.create }}</v-btn>
            </div>
          </div>

        </div>
      </template>

      <template v-slot:item.id="{ item }">
        <div class="font-weight-bold"># {{ item.id }}</div>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="d-flex justify-space-between align-center">
          <!-- Name on the left with a fixed or max width -->
          <div class="text-start name-container clickable-simple"> <!-- Adjust the margin-right here -->
            <router-link v-if="item.name.length < 86" class="text-decoration-none" :to="{ name: 'moduleEdit', params: { id: item.id } }">
              <span class="font-weight-regular color-primary">{{ item.name }}</span>
            </router-link>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <router-link
                    :data-cy="`module-edit-${item.id}`"
                    class="text-decoration-none"
                    :to="{ name: 'moduleEdit', params: { id: item.id } }"
                  >
                    <span class="font-weight-regular color-primary">{{ item.name.substring(0, 62) }}...</span>
                  </router-link>
                </div>
              </template>
              <span>{{ item.name }}</span>
            </v-tooltip>
          </div>
        </div>
      </template>

      <template v-slot:item.createdOn="{ item }">
        <div>{{ item.createdOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.modifiedOn="{ item }">
        <div>{{ item.modifiedOn | formatDateTime }}</div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { bus } from '@/main'
import { debounce } from 'lodash'
export default {
  props: {
    items: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      lock: true,
      showTypeSelector: false,
      search: '',
      options: {},
      totalItems: 0,
      headers: [
        {
          text: this.$lang.labels.name,
          align: 'start',
          sortable: true,
          value: 'name'
        }
      ],
      roles: []
    }
  },
  computed: {
    ...mapState('app', ['rowsPerPageItemsGlobal', 'userRoles', 'userSettings'])
  },
  watch: {
    options: {
      handler () {
        if (!this.lock) this.$emit('fetchModules', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '' })
        this.savePreFill()
      },
      deep: true
    },
    search: {
      handler: debounce( function (val) {
        if (val && val.length > 1 && !this.lock) this.$emit('fetchModules', { options: this.options, search: val, resetPage: true })
        if (!this.lock && (val === null || val === '')) this.$emit('fetchModules', { options: this.options, search: '', resetPage: true })
        this.savePreFill()
        this.options.page = 1
      }, 500)
    }
  },
  mounted() {
    bus.$on('refreshData', (name) => {
      if (!this.lock && name === this.$route.name) this.$emit('fetchModules', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true })
      this.options.page = 1
    })
  },
  created() {
    this.roles = this.userRoles.map((x) => x.name) || []

    if (this.userSettings.display.showModifiedOn) {
      this.headers.splice(1, 0, { text: this.$lang.labels.modifiedOn, value: 'modifiedOn' })
    }

    if (this.userSettings.display.showCreatedOn) {
      this.headers.splice(1, 0, { text: this.$lang.labels.createdOn, value: 'createdOn' })
    }

    if (this.userSettings.display.showId) {
      this.headers.splice(0, 0, { text: this.$lang.header.id, value: 'id' })
    }

    if (localStorage.preFillData) {
      const preFill = JSON.parse(localStorage.preFillData)

      if (preFill && preFill.moduleList) {
        this.search = preFill.moduleList.search
        this.options = preFill.moduleList.options
      }
    }
    this.$emit('fetchModules', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true })
    setTimeout(() => this.lock = false, 100)
    this.options.page = 1
  },
  methods: {
    savePreFill() {
      if (!localStorage.preFillData) localStorage.preFillData = '{}'
      if (localStorage.preFillData) {
        const preFill = JSON.parse(localStorage.preFillData)

        if (!preFill.moduleList) preFill.moduleList = {
          search: '',
          options: {
            page: 1,
            itemsPerPage: 25,
            sortBy: [],
            sortDesc: []
          }
        }

        preFill.moduleList.search = this.search
        preFill.moduleList.options = { ...this.options, page: 1 }

        localStorage.preFillData = JSON.stringify(preFill)
      }
    }
  }
}
</script>
